import React, { useEffect, useMemo, useState } from 'react';
import { Loan, FilterState, SortState, SortField } from './types/loan';
import { mockLoans } from './utils/mockData';
import { exportLoansToCSV } from './utils/exportToExcel';
import { FilterControls } from './components/FilterControls';
import { SummaryCards } from './components/SummaryCards';
import { LoanTable } from './components/LoanTable';
import { DownloadIcon, UsersIcon, CheckIcon, XIcon } from 'lucide-react';
export function App() {
  // State
  const [filters, setFilters] = useState<FilterState>({
    currentMarketRate: 6.0,
    startDate: '2020-01-01',
    endDate: new Date().toISOString().split('T')[0],
    rateThreshold: 1.0
  });
  const [selectedLoanIds, setSelectedLoanIds] = useState<Set<string>>(new Set());
  const [sortState, setSortState] = useState<SortState>({
    field: 'rateDifference',
    direction: 'desc'
  });
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  // Clear toast after 3 seconds
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);
  // Handlers
  const handleApplyFilters = () => {
    setSelectedLoanIds(new Set()); // Clear selection on manual apply
  };
  const handleSort = (field: SortField) => {
    setSortState(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };
  const handleToggleSelection = (loanId: string) => {
    setSelectedLoanIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(loanId)) {
        newSet.delete(loanId);
      } else {
        newSet.add(loanId);
      }
      return newSet;
    });
  };
  const handleToggleAll = (selectAll: boolean) => {
    if (selectAll) {
      setSelectedLoanIds(new Set(filteredAndSortedLoans.map(l => l.id)));
    } else {
      setSelectedLoanIds(new Set());
    }
  };
  const handleCreateLead = (loan: Loan) => {
    setToast({
      message: `Lead created for ${loan.borrowerName} (${loan.id})`,
      type: 'success'
    });
  };
  const handleBulkCreateLeads = () => {
    if (selectedLoanIds.size === 0) return;
    setToast({
      message: `Successfully created ${selectedLoanIds.size} leads!`,
      type: 'success'
    });
    setSelectedLoanIds(new Set());
  };
  const handleExportExcel = () => {
    const loansToExport = selectedLoanIds.size > 0 ? filteredAndSortedLoans.filter(l => selectedLoanIds.has(l.id)) : filteredAndSortedLoans;
    if (loansToExport.length === 0) {
      setToast({
        message: 'No loans to export',
        type: 'error'
      });
      return;
    }
    exportLoansToCSV(loansToExport, filters.currentMarketRate);
    setToast({
      message: `Exported ${loansToExport.length} loans to CSV`,
      type: 'success'
    });
  };
  // Derived Data — filters apply immediately
  const filteredAndSortedLoans = useMemo(() => {
    // 1. Filter
    let result = mockLoans.filter(loan => {
      const rateDiff = loan.interestRate - filters.currentMarketRate;
      const meetsThreshold = rateDiff >= filters.rateThreshold;
      const afterStart = loan.closeDate >= filters.startDate;
      const beforeEnd = loan.closeDate <= filters.endDate;
      return meetsThreshold && afterStart && beforeEnd;
    });
    // 2. Sort
    result.sort((a, b) => {
      let valA: any = a[sortState.field as keyof Loan];
      let valB: any = b[sortState.field as keyof Loan];
      if (sortState.field === 'rateDifference') {
        valA = a.interestRate - filters.currentMarketRate;
        valB = b.interestRate - filters.currentMarketRate;
      }
      if (valA < valB) return sortState.direction === 'asc' ? -1 : 1;
      if (valA > valB) return sortState.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return result;
  }, [filters, sortState]);
  return <div className="min-h-screen bg-gray-50 text-gray-900 font-sans pb-12" data-id="element-0">
      {/* Header */}
      <header className="bg-white sticky top-0 z-10 shadow-sm" data-id="element-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between" data-id="element-2">
          <div className="flex items-center gap-4" data-id="element-3">
            <img src="https://cdn.magicpatterns.com/uploads/2jfwCeyscqPo2Wm8HxYrkc/Zoom_Logo_New.png" alt="Zoom Home Lending" className="h-9" data-id="element-4" />
            <div className="hidden sm:block w-px h-6 bg-gray-200" data-id="element-5"></div>
            <span className="hidden sm:block text-xs uppercase tracking-wide text-gray-400 font-medium" data-id="element-6">
              Refinance Opportunity Finder
            </span>
          </div>
          <div className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-xs font-medium" data-id="element-7">
            Loan Officer Portal
          </div>
        </div>
        <div className="h-0.5 w-full bg-gradient-to-r from-violet-600 via-blue-500 to-cyan-400" data-id="element-8"></div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-9">
        {/* Toast Notification */}
        {toast && <div className={`fixed top-20 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-xl border animate-toast-in ${toast.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`} data-id="element-10">
            {toast.type === 'success' ? <CheckIcon className="w-5 h-5 text-green-500" data-id="element-11" /> : <XIcon className="w-5 h-5 text-red-500" data-id="element-12" />}
            <span className="font-medium" data-id="element-13">{toast.message}</span>
            <button onClick={() => setToast(null)} className="ml-2 text-gray-400 hover:text-gray-600" data-id="element-14">
              <XIcon className="w-4 h-4" data-id="element-15" />
            </button>
          </div>}

        {/* Filters */}
        <FilterControls filters={filters} onFilterChange={setFilters} onApplyFilters={handleApplyFilters} data-id="element-16" />

        {/* Summaries */}
        <SummaryCards loans={filteredAndSortedLoans} currentMarketRate={filters.currentMarketRate} data-id="element-17" />

        {/* Table Actions & Content */}
        <div className="space-y-3" data-id="element-18">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-19">
            <h2 className="text-lg font-semibold text-gray-800" data-id="element-20">
              Opportunities ({filteredAndSortedLoans.length})
              {selectedLoanIds.size > 0 && <span className="text-violet-600 ml-2 text-sm font-normal" data-id="element-21">
                  {selectedLoanIds.size} selected
                </span>}
            </h2>

            <div className="flex items-center gap-3 w-full sm:w-auto" data-id="element-22">
              <button onClick={handleExportExcel} className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-4 h-10 rounded-lg text-sm font-medium transition-colors focus:ring-2 focus:ring-offset-2 focus:ring-gray-200 shadow-sm" data-id="element-23">
                <DownloadIcon className="w-4 h-4" data-id="element-24" />
                Export {selectedLoanIds.size > 0 ? 'Selected' : 'All'}
              </button>

              <button onClick={handleBulkCreateLeads} disabled={selectedLoanIds.size === 0} className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 h-10 rounded-lg text-sm font-medium transition-all shadow-sm ${selectedLoanIds.size > 0 ? 'bg-gradient-to-r from-violet-600 to-cyan-500 hover:brightness-110 text-white focus:ring-2 focus:ring-offset-2 focus:ring-violet-500' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-25">
                <UsersIcon className="w-4 h-4" data-id="element-26" />
                Create Leads ({selectedLoanIds.size})
              </button>
            </div>
          </div>

          <LoanTable loans={filteredAndSortedLoans} currentMarketRate={filters.currentMarketRate} selectedLoanIds={selectedLoanIds} onToggleSelection={handleToggleSelection} onToggleAll={handleToggleAll} sortState={sortState} onSort={handleSort} onCreateLead={handleCreateLead} data-id="element-27" />
        </div>
      </main>
    </div>;
}